// Slice 14 — BesideU Admin prompt-drafting surface (DESIGN §5.5 / §9.3 #11).
//
// Closes the drafting half of the "Super Admin Prompt UI with approval
// workflow" DoD bullet. The CGC approval UI lives in `CgcDashboard.tsx`
// (Slice 12); activation lives there too (Slice 13). This route owns the
// upstream half: create a new draft, revise an existing one into a new
// version, or edit an in-place pristine draft (allowed only while the row
// has zero CGC decisions filed against it).
//
// The page mirrors the CGC dashboard's prompt-card layout so an admin
// reviewing the same row sees the familiar shape, then exposes the
// three write actions inline. Per-card affordances:
//   * Pristine row (no decisions yet) → Edit + Revise + View
//   * Non-pristine row (any decision filed) → Revise + View
//   * Active / inactive rows → Revise + View
// Edit on a non-pristine row is disabled with explanatory copy; an attempt
// to PATCH server-side returns 409 not_pristine which is rendered here.
//
// Slice 25 — read-affordances overlay: URL-mirrored filter/search/sort,
// flat vs by-mode grouping, and an inline previous-version diff viewer
// for prompts with a `previousVersionId`. All client-side over the same
// `/api/admin/prompts` list endpoint — no schema or API surface change.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { diffLines } from 'diff';
import {
  PROMPT_DRAFT_MODES,
  type AdminBoilerplateResponse,
  type AdminOrgSummary,
  type AdminPromptHistoryEntry,
  type AdminPromptSummary,
  type AdminPromptDetail,
  type BoilerplateBlock,
  type CreatePromptDraftRequest,
  type EditPromptDraftRequest,
  type RevisePromptDraftRequest,
} from '@besideu/shared/admin';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';

type PromptMode = (typeof PROMPT_DRAFT_MODES)[number];

// Derived lifecycle state — distinct from `isActive`. Drives the filter
// chip and the LifecycleBadges grouping. `inactive` here matches the CGC
// dashboard's `'inactive'` derivation (a row that WAS active and got
// displaced — no longer reactivable without governance escalation).
type LifecycleState =
  | 'draft' // no approvals at all
  | 'partial' // approvedBy set, but joint slot still empty when required
  | 'approved' // fully approved, not yet activated
  | 'active' // currently in production
  | 'inactive'; // historically active but displaced

function deriveLifecycle(p: AdminPromptSummary): LifecycleState {
  if (p.isActive) return 'active';
  if (p.deactivatedAt) return 'inactive';
  // We can't tell from the summary alone whether a mode requires joint
  // approval, so `partial` here means "first approval recorded, no joint
  // yet". For single-approval modes this state should be transient — the
  // backfill into `approved` happens on the same request as the approve
  // mutation. Treating it as a separate filter bucket matches what an
  // admin actually wants to see ("which drafts are still mid-review?").
  if (p.approvedBy && !p.approvedJointlyBy) return 'partial';
  if (p.approvedBy) return 'approved';
  return 'draft';
}

const LIFECYCLE_OPTIONS: ReadonlyArray<{ value: LifecycleState | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'partial', label: 'Partial approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const SORT_OPTIONS = [
  { value: 'recent', label: 'Recent activity' },
  { value: 'mode', label: 'Mode A→Z' },
  { value: 'version', label: 'Version' },
] as const;
type SortKey = (typeof SORT_OPTIONS)[number]['value'];

// Slice 30 — `lastActivityAt` is server-computed in `listAll` as
// `max(createdAt, MAX(decisions.createdAt), MAX(audit.createdAt for this
// promptId), MAX(activations.createdAt))` using the existing
// `(promptId, createdAt)` indexes on the three audit-adjacent tables.
// Pre-Slice-30 this lived as a client-side approximation over five
// summary timestamps; the server-side version is exact.
function lastActivityAtMs(p: AdminPromptSummary): number {
  return new Date(p.lastActivityAt).getTime();
}

// Naïve semver split — the schema regex permits a `-suffix` tail, but the
// pre-release ordering rules are not load-bearing here. Sort numerics only.
function semverParts(version: string): [number, number, number] {
  const match = /^(\d+)\.(\d+)\.(\d+)/.exec(version);
  if (!match) return [0, 0, 0];
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

function compareVersion(a: string, b: string): number {
  const [a1, a2, a3] = semverParts(a);
  const [b1, b2, b3] = semverParts(b);
  return b1 - a1 || b2 - a2 || b3 - a3;
}

export function AdminPrompts() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const prompts = useQuery({
    queryKey: ['admin', 'prompts'],
    queryFn: () => api.get<{ prompts: AdminPromptSummary[] }>('/api/admin/prompts'),
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const modeFilter = (searchParams.get('mode') ?? 'all') as PromptMode | 'all';
  const lifecycleFilter = (searchParams.get('lifecycle') ?? 'all') as LifecycleState | 'all';
  const orgFilter = searchParams.get('org') ?? 'all'; // 'all' | 'global' | <orgId>
  const queryText = searchParams.get('q') ?? '';
  const sortKey = (
    SORT_OPTIONS.find((s) => s.value === searchParams.get('sort')) ?? SORT_OPTIONS[0]
  ).value;
  const groupBy = searchParams.get('group') === 'mode' ? 'mode' : 'flat';

  function patchParams(updates: Record<string, string | null>) {
    const sp = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(updates)) {
      if (v === null || v === '') sp.delete(k);
      else sp.set(k, v);
    }
    setSearchParams(sp, { replace: true });
  }

  // Unique org options derived from the loaded prompts. Slice B will swap
  // this for a real org picker tied to /api/admin/orgs.
  const orgOptions = useMemo(() => {
    const all = prompts.data?.prompts ?? [];
    const seen = new Map<string, string>();
    for (const p of all) {
      if (p.organizationId) seen.set(p.organizationId, p.organizationName ?? p.organizationId);
    }
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [prompts.data]);

  const filtered = useMemo(() => {
    const all = prompts.data?.prompts ?? [];
    const q = queryText.trim().toLowerCase();
    return all.filter((p) => {
      if (modeFilter !== 'all' && p.mode !== modeFilter) return false;
      if (lifecycleFilter !== 'all' && deriveLifecycle(p) !== lifecycleFilter) return false;
      if (orgFilter === 'global' && p.organizationId !== null) return false;
      if (orgFilter !== 'all' && orgFilter !== 'global' && p.organizationId !== orgFilter)
        return false;
      if (!q) return true;
      const haystacks = [
        p.mode,
        p.countryCode,
        p.version,
        p.organizationName ?? '',
        p.changeSummary ?? '',
        p.promptTextPreview,
      ];
      return haystacks.some((h) => h.toLowerCase().includes(q));
    });
  }, [prompts.data, modeFilter, lifecycleFilter, orgFilter, queryText]);

  const sorted = useMemo(() => {
    const list = filtered.slice();
    if (sortKey === 'mode') {
      list.sort((a, b) => a.mode.localeCompare(b.mode) || compareVersion(a.version, b.version));
    } else if (sortKey === 'version') {
      list.sort((a, b) => compareVersion(a.version, b.version) || a.mode.localeCompare(b.mode));
    } else {
      list.sort((a, b) => lastActivityAtMs(b) - lastActivityAtMs(a));
    }
    return list;
  }, [filtered, sortKey]);

  const totalCount = prompts.data?.prompts.length ?? 0;
  const filteredCount = sorted.length;

  return (
    <section className="space-y-8">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">System prompts</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Draft and revise Maeve system prompts. Drafts are reviewed and approved by the Clinical
            Governance Council before activation.
          </p>
        </div>
        <Button variant="primary" onClick={() => setCreateOpen((v) => !v)}>
          {createOpen ? 'Cancel new draft' : 'New draft'}
        </Button>
      </header>

      <BoilerplateInspector />

      {createOpen ? (
        <section className="rounded-lg border border-border bg-muted/20 p-5">
          <h2 className="text-base font-semibold">New draft</h2>
          <CreateForm
            onCancel={() => setCreateOpen(false)}
            onCreated={() => {
              setCreateOpen(false);
              void queryClient.invalidateQueries({ queryKey: ['admin', 'prompts'] });
            }}
          />
        </section>
      ) : null}

      <section>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="text-lg font-semibold">All prompts</h2>
          {totalCount > 0 ? (
            <p className="text-xs text-muted-foreground">
              {filteredCount === totalCount
                ? `${totalCount} prompt${totalCount === 1 ? '' : 's'}`
                : `${filteredCount} of ${totalCount} prompts shown`}
            </p>
          ) : null}
        </div>

        {totalCount > 0 ? (
          <FilterBar
            modeFilter={modeFilter}
            lifecycleFilter={lifecycleFilter}
            orgFilter={orgFilter}
            queryText={queryText}
            sortKey={sortKey}
            groupBy={groupBy}
            orgOptions={orgOptions}
            onChange={patchParams}
          />
        ) : null}

        {prompts.isPending ? (
          <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
        ) : prompts.error ? (
          <p className="mt-4 text-sm text-traffic-red" role="alert">
            Could not load prompts.
          </p>
        ) : totalCount === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
            No prompts yet. Use “New draft” to author the first one.
          </div>
        ) : filteredCount === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
            No prompts match the current filters.
          </div>
        ) : groupBy === 'mode' ? (
          <GroupedPromptList prompts={sorted} />
        ) : (
          <ul className="mt-4 space-y-3">
            {sorted.map((p) => (
              <PromptCard key={p.id} prompt={p} />
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

// =============================================================================
// Filter / search / sort UI — URL-mirrored via `useSearchParams`.
// =============================================================================

function FilterBar({
  modeFilter,
  lifecycleFilter,
  orgFilter,
  queryText,
  sortKey,
  groupBy,
  orgOptions,
  onChange,
}: {
  modeFilter: PromptMode | 'all';
  lifecycleFilter: LifecycleState | 'all';
  orgFilter: string;
  queryText: string;
  sortKey: SortKey;
  groupBy: 'flat' | 'mode';
  orgOptions: ReadonlyArray<[string, string]>;
  onChange: (updates: Record<string, string | null>) => void;
}) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <select
        aria-label="Filter by mode"
        className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        value={modeFilter}
        onChange={(e) => onChange({ mode: e.target.value === 'all' ? null : e.target.value })}
      >
        <option value="all">All modes</option>
        {PROMPT_DRAFT_MODES.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
      <select
        aria-label="Filter by lifecycle"
        className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        value={lifecycleFilter}
        onChange={(e) => onChange({ lifecycle: e.target.value === 'all' ? null : e.target.value })}
      >
        {LIFECYCLE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <select
        aria-label="Filter by organisation scope"
        className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        value={orgFilter}
        onChange={(e) => onChange({ org: e.target.value === 'all' ? null : e.target.value })}
      >
        <option value="all">All scopes</option>
        <option value="global">Global only</option>
        {orgOptions.map(([id, name]) => (
          <option key={id} value={id}>
            {name}
          </option>
        ))}
      </select>
      <input
        type="search"
        value={queryText}
        onChange={(e) => onChange({ q: e.target.value === '' ? null : e.target.value })}
        placeholder="Search text, change summary, org…"
        className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none sm:max-w-xs"
      />
      <select
        aria-label="Sort"
        className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        value={sortKey}
        onChange={(e) => onChange({ sort: e.target.value === 'recent' ? null : e.target.value })}
      >
        {SORT_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            Sort: {o.label}
          </option>
        ))}
      </select>
      <div
        className="inline-flex rounded-md border border-border bg-background p-0.5 text-xs"
        role="group"
        aria-label="View"
      >
        <button
          type="button"
          aria-pressed={groupBy === 'flat'}
          onClick={() => onChange({ group: null })}
          className={`rounded px-2.5 py-1 ${groupBy === 'flat' ? 'bg-primary/10 text-primary' : 'text-muted-foreground'}`}
        >
          Flat
        </button>
        <button
          type="button"
          aria-pressed={groupBy === 'mode'}
          onClick={() => onChange({ group: 'mode' })}
          className={`rounded px-2.5 py-1 ${groupBy === 'mode' ? 'bg-primary/10 text-primary' : 'text-muted-foreground'}`}
        >
          By mode
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// By-mode grouping — collapses the filtered list into one card per mode,
// with version chains visible (newest first). Mirrors how the CGC reviewer
// thinks about a mode lifecycle.
// =============================================================================

function GroupedPromptList({ prompts }: { prompts: AdminPromptSummary[] }) {
  const groups = useMemo(() => {
    const byMode = new Map<PromptMode, AdminPromptSummary[]>();
    for (const p of prompts) {
      const existing = byMode.get(p.mode);
      if (existing) existing.push(p);
      else byMode.set(p.mode, [p]);
    }
    const list = Array.from(byMode.entries());
    list.sort((a, b) => a[0].localeCompare(b[0]));
    for (const [, rows] of list) rows.sort((a, b) => compareVersion(a.version, b.version));
    return list;
  }, [prompts]);

  return (
    <div className="mt-4 space-y-6">
      {groups.map(([mode, rows]) => {
        const active = rows.find((r) => r.isActive);
        return (
          <section key={mode} className="rounded-lg border border-border p-5">
            <header className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-base font-semibold">{mode}</h3>
              <p className="text-xs text-muted-foreground">
                {rows.length} version{rows.length === 1 ? '' : 's'}
                {active ? ` · active: v${active.version}` : ' · no active version'}
              </p>
            </header>
            <ul className="mt-3 space-y-2">
              {rows.map((p) => (
                <PromptCard key={p.id} prompt={p} variant="compact" />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

// =============================================================================
// New draft form
// =============================================================================

function CreateForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const [mode, setMode] = useState<CreatePromptDraftRequest['mode']>('HR_Maeve');
  const [countryCode, setCountryCode] = useState('UK');
  const [organizationId, setOrganizationId] = useState('');
  const [version, setVersion] = useState('');
  const [promptText, setPromptText] = useState('');
  const [changeSummary, setChangeSummary] = useState('');

  const create = useMutation({
    mutationFn: (body: CreatePromptDraftRequest) =>
      api.post<{ prompt: AdminPromptDetail }>('/api/admin/prompts', body),
    onSuccess: () => onCreated(),
  });

  return (
    <form
      className="mt-4 grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate({
          mode,
          countryCode: countryCode.toUpperCase(),
          organizationId: organizationId.trim() === '' ? null : organizationId.trim(),
          promptText,
          version,
          changeSummary: changeSummary.trim() === '' ? undefined : changeSummary.trim(),
        });
      }}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <label className="block text-sm">
          <span className="text-xs tracking-wide text-muted-foreground uppercase">Mode</span>
          <select
            className="mt-1 block w-full rounded-md border border-border bg-background px-2 py-2"
            value={mode}
            onChange={(e) => setMode(e.target.value as CreatePromptDraftRequest['mode'])}
          >
            {PROMPT_DRAFT_MODES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-xs tracking-wide text-muted-foreground uppercase">
            Country (ISO 3166-1 α-2)
          </span>
          <input
            className="mt-1 block w-full rounded-md border border-border bg-background px-2 py-2"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            maxLength={2}
            required
          />
        </label>
        <label className="block text-sm">
          <span className="text-xs tracking-wide text-muted-foreground uppercase">Version</span>
          <input
            className="mt-1 block w-full rounded-md border border-border bg-background px-2 py-2 font-mono text-sm"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="1.0.0"
            required
          />
        </label>
      </div>

      <OrgScopePicker value={organizationId} onChange={setOrganizationId} />

      <label className="block text-sm">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-xs tracking-wide text-muted-foreground uppercase">
            Prompt text (≥50 chars)
          </span>
          <InsertBoilerplateButton
            onInsert={(s) => setPromptText((p) => (p ? `${p}\n\n${s}` : s))}
          />
        </div>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-3 font-mono text-xs"
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
          rows={14}
          minLength={50}
          maxLength={50000}
          required
        />
      </label>

      <label className="block text-sm">
        <span className="text-xs tracking-wide text-muted-foreground uppercase">
          Change summary (optional for new drafts; required for revisions)
        </span>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-3 text-sm"
          value={changeSummary}
          onChange={(e) => setChangeSummary(e.target.value)}
          rows={2}
          maxLength={2000}
        />
      </label>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" loading={create.isPending}>
          Create draft
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {create.error ? <WriteError error={create.error} verb="create" /> : null}
    </form>
  );
}

// =============================================================================
// Per-prompt card (View / Edit / Revise)
// =============================================================================

function PromptCard({
  prompt,
  variant = 'full',
}: {
  prompt: AdminPromptSummary;
  variant?: 'full' | 'compact';
}) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [revising, setRevising] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Detail fetch is lazy — only when the card is expanded does the full
  // text + decision count come down. Mirrors the CGC dashboard pattern.
  const detail = useQuery({
    queryKey: ['admin', 'prompts', prompt.id],
    queryFn: () => api.get<{ prompt: AdminPromptDetail }>(`/api/admin/prompts/${prompt.id}`),
    enabled: expanded || editing || revising,
  });

  // Previous-version fetch — only when the user has opened the diff view.
  // Shares the same React Query cache as the main detail, so opening the
  // previous version from another card is free.
  const previous = useQuery({
    queryKey: ['admin', 'prompts', prompt.previousVersionId],
    queryFn: () =>
      api.get<{ prompt: AdminPromptDetail }>(`/api/admin/prompts/${prompt.previousVersionId}`),
    enabled: diffOpen && prompt.previousVersionId !== null,
  });

  function closeForms() {
    setEditing(false);
    setRevising(false);
    setDiffOpen(false);
    setHistoryOpen(false);
  }

  return (
    <li
      className={
        variant === 'compact'
          ? 'rounded-md border border-border p-4'
          : 'rounded-lg border border-border p-5'
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-medium">
            {prompt.mode} <span className="text-xs text-muted-foreground">v{prompt.version}</span>{' '}
            <span className="text-xs text-muted-foreground">
              · {prompt.countryCode} · {prompt.organizationName ?? 'global'}
            </span>
          </p>
          <LifecycleBadges prompt={prompt} />
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            className="py-1 text-sm"
            onClick={() => {
              if (expanded) closeForms();
              setExpanded(!expanded);
            }}
          >
            {expanded ? 'Collapse' : 'View'}
          </Button>
        </div>
      </div>

      {variant === 'full' ? (
        <p className="mt-3 line-clamp-3 text-sm whitespace-pre-wrap text-muted-foreground">
          {prompt.promptTextPreview}
        </p>
      ) : null}

      {expanded ? (
        <div className="mt-4 space-y-4 border-t border-border pt-4">
          {detail.isPending ? (
            <p className="text-sm text-muted-foreground">Loading full text…</p>
          ) : detail.error ? (
            <p className="text-sm text-traffic-red" role="alert">
              Could not load prompt detail.
            </p>
          ) : (
            <>
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs tracking-wide text-muted-foreground uppercase">Full text</p>
                  {prompt.previousVersionId ? (
                    <Button
                      variant="ghost"
                      className="py-0.5 text-xs"
                      onClick={() => setDiffOpen((v) => !v)}
                    >
                      {diffOpen ? 'Hide diff' : 'Compare to previous version'}
                    </Button>
                  ) : null}
                </div>
                {diffOpen && prompt.previousVersionId ? (
                  previous.isPending ? (
                    <p className="mt-2 text-xs text-muted-foreground">Loading previous version…</p>
                  ) : previous.error ? (
                    <p className="mt-2 text-xs text-traffic-red" role="alert">
                      Could not load the previous version.
                    </p>
                  ) : (
                    <DiffViewer
                      oldVersion={previous.data.prompt.version}
                      oldText={previous.data.prompt.promptText}
                      newVersion={detail.data.prompt.version}
                      newText={detail.data.prompt.promptText}
                    />
                  )
                ) : (
                  <pre className="mt-1 max-h-96 overflow-y-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-xs whitespace-pre-wrap">
                    {detail.data.prompt.promptText}
                  </pre>
                )}
              </div>
              {prompt.changeSummary ? (
                <div>
                  <p className="text-xs tracking-wide text-muted-foreground uppercase">
                    Change summary
                  </p>
                  <p className="mt-1 text-sm">{prompt.changeSummary}</p>
                </div>
              ) : null}

              <div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs tracking-wide text-muted-foreground uppercase">History</p>
                  <Button
                    variant="ghost"
                    className="py-0.5 text-xs"
                    onClick={() => setHistoryOpen((v) => !v)}
                  >
                    {historyOpen ? 'Hide history' : 'Show full history'}
                  </Button>
                </div>
                {historyOpen ? (
                  <div className="mt-2">
                    <HistoryTimeline promptId={prompt.id} />
                  </div>
                ) : null}
              </div>
            </>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              className="py-1.5 text-sm"
              disabled={!prompt.isPristine}
              title={
                prompt.isPristine
                  ? undefined
                  : `${prompt.decisionCount} CGC decision(s) filed — edit is locked. Use Revise to create a new version.`
              }
              onClick={() => {
                setEditing((v) => !v);
                setRevising(false);
              }}
            >
              {editing ? 'Cancel edit' : 'Edit'}
            </Button>
            <Button
              variant="secondary"
              className="py-1.5 text-sm"
              onClick={() => {
                setRevising((v) => !v);
                setEditing(false);
              }}
            >
              {revising ? 'Cancel revise' : 'Revise (new version)'}
            </Button>
          </div>

          {editing && detail.data ? (
            <EditForm
              prompt={detail.data.prompt}
              onDone={() => {
                setEditing(false);
              }}
            />
          ) : null}

          {revising && detail.data ? (
            <ReviseForm
              prompt={detail.data.prompt}
              onDone={() => {
                setRevising(false);
              }}
            />
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function LifecycleBadges({ prompt }: { prompt: AdminPromptSummary }) {
  const badges: Array<{ label: string; tone: 'pending' | 'partial' | 'ready' | 'live' }> = [];
  if (prompt.isActive) badges.push({ label: 'Active', tone: 'live' });
  else if (prompt.deactivatedAt) badges.push({ label: 'Inactive (historical)', tone: 'pending' });
  else if (prompt.approvedJointlyBy) badges.push({ label: 'Approved (joint)', tone: 'ready' });
  else if (prompt.approvedBy) badges.push({ label: 'Partially approved', tone: 'partial' });
  else badges.push({ label: 'Awaiting CGC review', tone: 'pending' });

  if (prompt.decisionCount > 0) {
    badges.push({
      label: `${prompt.decisionCount} decision${prompt.decisionCount === 1 ? '' : 's'} filed`,
      tone: 'partial',
    });
  } else {
    badges.push({ label: 'Pristine (editable)', tone: 'ready' });
  }

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {badges.map((b) => (
        <span
          key={b.label}
          className={[
            'inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium tracking-normal uppercase',
            b.tone === 'pending' ? 'bg-muted text-muted-foreground' : '',
            b.tone === 'partial' ? 'bg-traffic-amber/10 text-traffic-amber-text' : '',
            b.tone === 'ready' ? 'bg-traffic-green/10 text-traffic-green-text' : '',
            b.tone === 'live' ? 'bg-primary/10 text-primary' : '',
          ].join(' ')}
        >
          {b.label}
        </span>
      ))}
    </div>
  );
}

// =============================================================================
// Boilerplate inspector + insert helper — Slice 27.
//
// The §4.7 boilerplate blocks live as `Boilerplate_*` rows in `system_prompts`
// (active row → DB-sourced; no active row → code-constant fallback). This
// hook fetches the resolved blocks; both the inspector panel and the
// `<InsertBoilerplateButton>` consume it.
// =============================================================================

function useBoilerplate() {
  return useQuery({
    queryKey: ['admin', 'boilerplate'],
    queryFn: () => api.get<AdminBoilerplateResponse>('/api/admin/boilerplate'),
    // Same posture as the org picker — the active boilerplate can change
    // when a CGC member activates a new version; surfacing the freshest
    // text matters more than save-a-fetch on this admin-only surface.
    staleTime: 30_000,
  });
}

// Joins the three blocks into one snippet suitable for pasting into a
// prompt body. Order matches the §4.7 reading order. For `prohibited_use`:
// the canonical text when DB-active; otherwise a synthetic fallback
// paragraph that includes all three legacy anchors verbatim — Slice 27
// review caught the pre-fix behaviour of inserting just the third anchor
// ("Diagnose or assess any mental health condition") as a bare sentence
// fragment, which satisfies the validator but reads as an incomplete §4.7.2
// section to a CGC reviewer. The synthetic paragraph passes validation
// (anchor `.some()` matches all three) and looks like a real block.
const PROHIBITED_USE_FALLBACK_SNIPPET =
  'Prohibited Use Cases (§4.7.2): Maeve must not be used to diagnose or assess any mental health condition, deliver therapeutic interventions in place of a qualified clinician, function as a crisis or emergency response service, or replace human safeguarding judgement.';

function boilerplateSnippet(blocks: AdminBoilerplateResponse['blocks']): string {
  const prohibited = blocks.prohibited_use.text ?? PROHIBITED_USE_FALLBACK_SNIPPET;
  return [blocks.intended_use.text ?? '', blocks.human_oversight.text ?? '', prohibited]
    .filter((s) => s.length > 0)
    .join('\n\n');
}

function BoilerplateInspector() {
  const boilerplate = useBoilerplate();

  if (boilerplate.isPending) {
    return (
      <section className="rounded-lg border border-border bg-muted/10 p-5">
        <h2 className="text-base font-semibold">Required §4.7 boilerplate</h2>
        <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
      </section>
    );
  }
  if (boilerplate.error || !boilerplate.data) {
    return (
      <section className="rounded-lg border border-border bg-muted/10 p-5">
        <h2 className="text-base font-semibold">Required §4.7 boilerplate</h2>
        <p className="mt-2 text-sm text-traffic-red" role="alert">
          Could not load boilerplate.
        </p>
      </section>
    );
  }

  const { blocks } = boilerplate.data;
  return (
    <section className="rounded-lg border border-border bg-muted/10 p-5">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Required §4.7 boilerplate</h2>
        <p className="text-xs text-muted-foreground">
          Every HR_Maeve, Manager_Guidance, and Employee_Companion prompt must include all three
          blocks (verbatim).
        </p>
      </header>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <BoilerplateCard label="Intended Use Statement" block={blocks.intended_use} />
        <BoilerplateCard label="Human Oversight Clause" block={blocks.human_oversight} />
        <BoilerplateCard label="Prohibited Use Cases" block={blocks.prohibited_use} />
      </div>
    </section>
  );
}

function BoilerplateCard({ label, block }: { label: string; block: BoilerplateBlock }) {
  const [copied, setCopied] = useState(false);
  const displayText =
    block.text ??
    (block.anchors && block.anchors.length > 0 ? block.anchors.join('\n— or —\n') : '(empty)');

  async function copy() {
    try {
      await navigator.clipboard.writeText(displayText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (insecure context / no permission); fall back is
      // a no-op — the text is selectable in the <pre> below.
    }
  }

  return (
    <div className="rounded-md border border-border bg-background p-3 text-xs">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-medium">{label}</p>
        <SourceBadge block={block} />
      </div>
      <pre className="mt-2 max-h-40 overflow-y-auto rounded border border-border bg-muted/30 p-2 whitespace-pre-wrap">
        {displayText}
      </pre>
      <button
        type="button"
        onClick={copy}
        className="mt-2 text-xs text-primary underline-offset-2 hover:underline"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function SourceBadge({ block }: { block: BoilerplateBlock }) {
  if (block.source === 'db') {
    return (
      <span className="rounded-full bg-traffic-green/10 px-2 py-0.5 text-[10px] font-medium tracking-normal text-traffic-green-text uppercase">
        DB v{block.version}
      </span>
    );
  }
  return (
    <span
      className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium tracking-normal text-muted-foreground uppercase"
      title="No active DB row — using @besideu/shared/maeve constants. Seed a Boilerplate_* prompt and have legal counsel approve + activate it to override."
    >
      Constant fallback
    </span>
  );
}

function InsertBoilerplateButton({ onInsert }: { onInsert: (snippet: string) => void }) {
  const boilerplate = useBoilerplate();
  const disabled = !boilerplate.data;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (!boilerplate.data) return;
        onInsert(boilerplateSnippet(boilerplate.data.blocks));
      }}
      className="text-xs text-primary underline-offset-2 hover:underline disabled:opacity-50"
      title="Append the three §4.7 boilerplate blocks (Intended Use, Human Oversight, Prohibited Use) to this textarea."
    >
      Insert §4.7 boilerplate
    </button>
  );
}

// =============================================================================
// Org scope picker — Slice 26. Replaces the pre-Slice-26 UUID textbox so the
// admin doesn't have to copy ids out of the orgs tab. Sources from the same
// `/api/admin/orgs` endpoint the Organisations tab uses; React Query cache
// is shared so navigating Orgs → Prompts (or back) is free.
//
// Value is the empty string for "Global (no org)" and a UUID for a specific
// org. The CreateForm above already normalises empty string to `null` for
// the request body, so the wire shape is unchanged.
// =============================================================================

function OrgScopePicker({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const orgs = useQuery({
    queryKey: ['admin', 'orgs'],
    queryFn: () => api.get<{ orgs: AdminOrgSummary[] }>('/api/admin/orgs'),
    // The picker is used in a write form — surface the freshest list. Orgs
    // tab and Prompts tab can be open in adjacent tabs; a freshly approved
    // org should appear here without a hard refresh.
    staleTime: 30_000,
  });

  return (
    <label className="block text-sm">
      <span className="text-xs tracking-wide text-muted-foreground uppercase">
        Organisation scope
      </span>
      <select
        className="mt-1 block w-full rounded-md border border-border bg-background px-2 py-2"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Global (no org)</option>
        {orgs.isPending ? (
          <option value="" disabled>
            Loading orgs…
          </option>
        ) : orgs.error || !orgs.data ? (
          <option value="" disabled>
            Could not load orgs — leaving global
          </option>
        ) : (
          orgs.data.orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
              {o.status !== 'Active' ? ` (${o.status})` : ''}
            </option>
          ))
        )}
      </select>
      <span className="mt-1 block text-xs text-muted-foreground">
        Leave as Global to apply across all orgs; pick an org to author a per-tenant override.
      </span>
    </label>
  );
}

// =============================================================================
// History timeline — Slice 26. Unified chronological view of admin draft
// actions, CGC decisions, and lifecycle events for a single prompt. Renders
// oldest first (the natural reading order for "what happened to this row").
// =============================================================================

function HistoryTimeline({ promptId }: { promptId: string }) {
  const history = useQuery({
    queryKey: ['admin', 'prompts', promptId, 'history'],
    queryFn: () =>
      api.get<{ history: AdminPromptHistoryEntry[] }>(`/api/admin/prompts/${promptId}/history`),
  });

  if (history.isPending) {
    return <p className="text-sm text-muted-foreground">Loading history…</p>;
  }
  if (history.error || !history.data) {
    return (
      <p className="text-sm text-traffic-red" role="alert">
        Could not load history.
      </p>
    );
  }
  if (history.data.history.length === 0) {
    return <p className="text-sm text-muted-foreground">No history yet.</p>;
  }

  return (
    <ol className="space-y-2">
      {history.data.history.map((entry, idx) => (
        <li
          key={`${entry.kind}:${entry.at}:${idx}`}
          className="rounded-md border border-border bg-muted/20 p-3 text-sm"
        >
          <HistoryEntry entry={entry} />
        </li>
      ))}
    </ol>
  );
}

function numberOr(value: unknown, fallback: string): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toString() : fallback;
}

function HistoryEntry({ entry }: { entry: AdminPromptHistoryEntry }) {
  const at = new Date(entry.at).toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  if (entry.kind === 'admin_action') {
    const label =
      entry.action === 'PromptDraft_Created'
        ? 'Draft created'
        : entry.action === 'PromptDraft_Revised'
          ? 'Revised (new version)'
          : 'In-place edit';
    return (
      <div className="space-y-1">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-medium">{label}</span>
          <span className="text-xs text-muted-foreground">{at}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          by {entry.actorAdminName ?? '(deleted admin)'} · BesideU Admin
        </p>
        {entry.action === 'PromptDraft_Edited' && entry.payload ? (
          <p className="text-xs">
            {numberOr(entry.payload.previousLength, '?')} → {numberOr(entry.payload.newLength, '?')}{' '}
            chars
          </p>
        ) : null}
      </div>
    );
  }

  if (entry.kind === 'cgc_decision') {
    const tone = entry.decision === 'approved' ? 'text-traffic-green-text' : 'text-traffic-red';
    return (
      <div className="space-y-1">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className={`font-medium ${tone}`}>
            {entry.decision === 'approved' ? 'Approved' : 'Rejected'}
          </span>
          <span className="text-xs text-muted-foreground">{at}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          by {entry.cgcMemberName ?? '(deleted member)'} · CGC (
          {entry.cgcMemberKind ? entry.cgcMemberKind.replaceAll('_', ' ') : 'unknown kind'})
        </p>
        {entry.reason ? <p className="text-xs whitespace-pre-wrap">{entry.reason}</p> : null}
      </div>
    );
  }

  // lifecycle — Slice 29 surfaces the actor when available (post-Slice-29
  // activations always record one). Pre-Slice-29 backfilled rows have no
  // actor, so the fallback copy makes that explicit rather than implying
  // the actor was anonymised on purpose.
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">
          {entry.event === 'activated' ? 'Activated in production' : 'Displaced (deactivated)'}
        </span>
        <span className="text-xs text-muted-foreground">{at}</span>
      </div>
      {entry.actorCgcMemberId ? (
        <p className="text-xs text-muted-foreground">
          by {entry.actorName ?? '(deleted member)'} · CGC (
          {entry.actorKind ? entry.actorKind.replaceAll('_', ' ') : 'unknown kind'})
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Actor not recorded (pre-Slice-29 backfilled event).
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Diff viewer — unified line-level diff between the previous version and the
// current one. Renders with red/green gutters and a small header summarising
// counts. We use line-level rather than character-level because system prompts
// are paragraph-structured and char-level diffs become illegible noise on
// large blocks of prose.
// =============================================================================

function DiffViewer({
  oldVersion,
  oldText,
  newVersion,
  newText,
}: {
  oldVersion: string;
  oldText: string;
  newVersion: string;
  newText: string;
}) {
  // diffLines returns chunks whose `value` is the concatenated text of all
  // consecutive same-kind lines, with a trailing `\n`. Flatten to per-line
  // rows so each line gets its own `+`/`−` gutter — without this, a removed
  // paragraph of N lines would only show a marker on the first visible line
  // and the others would look identical to context lines in the same tinted
  // background.
  const rows = useMemo(() => {
    const out: Array<{ kind: 'added' | 'removed' | 'context'; text: string }> = [];
    for (const c of diffLines(oldText, newText)) {
      const kind: 'added' | 'removed' | 'context' = c.added
        ? 'added'
        : c.removed
          ? 'removed'
          : 'context';
      const lines = c.value.split('\n');
      // diffLines includes a trailing newline on each chunk, which becomes
      // an empty string after split — drop it so we don't render a phantom
      // blank row at the end of every chunk.
      if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
      for (const text of lines) out.push({ kind, text });
    }
    return out;
  }, [oldText, newText]);

  const counts = useMemo(() => {
    let added = 0;
    let removed = 0;
    for (const r of rows) {
      if (r.kind === 'added') added++;
      else if (r.kind === 'removed') removed++;
    }
    return { added, removed };
  }, [rows]);

  return (
    <div className="mt-2 rounded-md border border-border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/30 px-3 py-1.5 text-xs">
        <span className="text-muted-foreground">
          v{oldVersion} → v{newVersion}
        </span>
        <span className="font-mono">
          <span className="text-traffic-green-text">+{counts.added}</span>{' '}
          <span className="text-traffic-red">−{counts.removed}</span>
        </span>
      </div>
      <div className="max-h-96 overflow-y-auto bg-background font-mono text-xs">
        {rows.map((r, idx) => {
          const tone =
            r.kind === 'added'
              ? 'bg-traffic-green/10 text-traffic-green-text border-traffic-green'
              : r.kind === 'removed'
                ? 'bg-traffic-red/10 text-traffic-red border-traffic-red'
                : 'text-muted-foreground border-transparent';
          const marker = r.kind === 'added' ? '+' : r.kind === 'removed' ? '−' : ' ';
          return (
            <div key={idx} className={`flex border-l-2 px-3 py-0.5 ${tone}`}>
              <span className="mr-2 w-3 shrink-0 select-none">{marker}</span>
              <span className="break-words whitespace-pre-wrap">{r.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// Edit form — in-place PATCH, only allowed for pristine rows
// =============================================================================

function EditForm({ prompt, onDone }: { prompt: AdminPromptDetail; onDone: () => void }) {
  const [promptText, setPromptText] = useState(prompt.promptText);
  const [changeSummary, setChangeSummary] = useState(prompt.changeSummary ?? '');
  const queryClient = useQueryClient();

  const edit = useMutation({
    mutationFn: (body: EditPromptDraftRequest) =>
      api.patch<{ prompt: AdminPromptDetail }>(`/api/admin/prompts/${prompt.id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'prompts'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'prompts', prompt.id] });
      onDone();
    },
  });

  return (
    <form
      className="mt-2 grid gap-3 rounded-md border border-dashed border-border bg-background p-4"
      onSubmit={(e) => {
        e.preventDefault();
        // Build a sparse patch: include only fields the user actually changed.
        const body: EditPromptDraftRequest = {};
        if (promptText !== prompt.promptText) body.promptText = promptText;
        const trimmed = changeSummary.trim();
        const original = prompt.changeSummary ?? '';
        if (trimmed !== original && trimmed.length > 0) body.changeSummary = trimmed;
        edit.mutate(body);
      }}
    >
      <p className="text-xs text-muted-foreground">
        In-place edit — only allowed while no CGC decisions exist on this row.
      </p>
      <label className="block text-sm">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-xs tracking-wide text-muted-foreground uppercase">Prompt text</span>
          <InsertBoilerplateButton
            onInsert={(s) => setPromptText((p) => (p ? `${p}\n\n${s}` : s))}
          />
        </div>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-3 font-mono text-xs"
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
          rows={14}
          minLength={50}
          maxLength={50000}
        />
      </label>
      <label className="block text-sm">
        <span className="text-xs tracking-wide text-muted-foreground uppercase">
          Change summary (optional)
        </span>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-3 text-sm"
          value={changeSummary}
          onChange={(e) => setChangeSummary(e.target.value)}
          rows={2}
          maxLength={2000}
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" loading={edit.isPending} className="py-1.5 text-sm">
          Save edit
        </Button>
        <Button type="button" variant="ghost" onClick={onDone} className="py-1.5 text-sm">
          Discard changes
        </Button>
      </div>
      {edit.error ? <WriteError error={edit.error} verb="edit" /> : null}
    </form>
  );
}

// =============================================================================
// Revise form — creates a new version row pointing back at this one
// =============================================================================

function ReviseForm({ prompt, onDone }: { prompt: AdminPromptDetail; onDone: () => void }) {
  const [promptText, setPromptText] = useState(prompt.promptText);
  const [version, setVersion] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const queryClient = useQueryClient();

  const revise = useMutation({
    mutationFn: (body: RevisePromptDraftRequest) =>
      api.post<{ prompt: AdminPromptDetail }>(`/api/admin/prompts/${prompt.id}/revise`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'prompts'] });
      onDone();
    },
  });

  return (
    <form
      className="mt-2 grid gap-3 rounded-md border border-dashed border-border bg-background p-4"
      onSubmit={(e) => {
        e.preventDefault();
        revise.mutate({ promptText, version, changeSummary });
      }}
    >
      <p className="text-xs text-muted-foreground">
        Revise — creates a new row linked to v{prompt.version}. The new version becomes the active
        draft once the CGC approves it.
      </p>
      <label className="block text-sm">
        <span className="text-xs tracking-wide text-muted-foreground uppercase">
          New version (must differ from v{prompt.version})
        </span>
        <input
          className="mt-1 block w-full rounded-md border border-border bg-background px-2 py-2 font-mono text-sm"
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          placeholder="2.0.0"
          required
        />
      </label>
      <label className="block text-sm">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-xs tracking-wide text-muted-foreground uppercase">Prompt text</span>
          <InsertBoilerplateButton
            onInsert={(s) => setPromptText((p) => (p ? `${p}\n\n${s}` : s))}
          />
        </div>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-3 font-mono text-xs"
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
          rows={14}
          minLength={50}
          maxLength={50000}
          required
        />
      </label>
      <label className="block text-sm">
        <span className="text-xs tracking-wide text-muted-foreground uppercase">
          Change summary (required)
        </span>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-3 text-sm"
          value={changeSummary}
          onChange={(e) => setChangeSummary(e.target.value)}
          rows={3}
          minLength={1}
          maxLength={2000}
          required
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          variant="primary"
          loading={revise.isPending}
          className="py-1.5 text-sm"
        >
          Submit revision
        </Button>
        <Button type="button" variant="ghost" onClick={onDone} className="py-1.5 text-sm">
          Cancel
        </Button>
      </div>
      {revise.error ? <WriteError error={revise.error} verb="revise" /> : null}
    </form>
  );
}

// =============================================================================
// Error rendering — common across create/edit/revise. Operator-facing copy
// keyed on the discriminated `error` field returned by the API. Anything
// unrecognised falls back to a generic message.
// =============================================================================

function WriteError({ error, verb }: { error: unknown; verb: 'create' | 'edit' | 'revise' }) {
  if (!(error instanceof ApiError)) {
    return (
      <p className="text-sm text-traffic-red" role="status">
        Could not {verb} the draft. Try again.
      </p>
    );
  }

  if (error.message === 'missing_boilerplate') {
    const raw = error.body?.missing;
    const missing: string[] = Array.isArray(raw) ? raw.filter((v) => typeof v === 'string') : [];
    return (
      <div className="rounded-md border border-traffic-red/30 bg-traffic-red/5 p-3 text-sm text-traffic-red">
        <p className="font-medium">Missing required §4.7 boilerplate</p>
        <ul className="mt-1 list-disc pl-5 text-xs">
          {missing.length === 0 ? (
            <li>One or more required boilerplate anchors are absent from the prompt body.</li>
          ) : (
            missing.map((m) => <li key={m}>{boilerplateLabel(m)}</li>)
          )}
        </ul>
        <p className="mt-2 text-xs">
          Maeve user-facing modes (HR_Maeve, Manager_Guidance, Employee_Companion) must include the
          Intended Use Statement, the Human Oversight clause, and the Prohibited Use Cases anchor
          before the CGC will approve the prompt.
        </p>
      </div>
    );
  }

  if (error.message === 'duplicate_version') {
    return (
      <p className="text-sm text-traffic-red" role="status">
        A prompt with this mode + scope already uses that version. Pick a higher version number.
      </p>
    );
  }

  if (error.message === 'invalid_organization') {
    return (
      <p className="text-sm text-traffic-red" role="status">
        The organisation id does not exist. Leave the field blank for a global prompt, or paste an
        id from the admin dashboard’s organisations table.
      </p>
    );
  }

  if (error.message === 'not_pristine') {
    const count = typeof error.body?.decisionCount === 'number' ? error.body.decisionCount : 0;
    return (
      <p className="text-sm text-traffic-red" role="status">
        This prompt has {count} CGC decision(s) filed against it and can no longer be edited
        in-place. Create a new version with “Revise” instead.
      </p>
    );
  }

  if (error.message === 'not_found') {
    return (
      <p className="text-sm text-traffic-red" role="status">
        That prompt no longer exists. The list will refresh on next reload.
      </p>
    );
  }

  return (
    <p className="text-sm text-traffic-red" role="status">
      Could not {verb} the draft. ({error.message})
    </p>
  );
}

function boilerplateLabel(key: string): string {
  switch (key) {
    case 'intended_use':
      return 'Intended Use Statement';
    case 'human_oversight':
      return 'Human Oversight clause';
    case 'prohibited_use':
      return 'Prohibited Use Cases anchor';
    default:
      return key;
  }
}
